import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Layout } from 'blaze-validator';
import ListView from './ListView';
import toastr from '../toastr';
import LoadingPage from '../LoadingPage';
import { modal } from '../modal/ModalEmitter';
import Socket from '../../services/SocketService';
import ApiService from '../../services/ApiService';
import { formatValue, getCellValue } from '../../util/grid';

class ListContainer extends Component {
  constructor(props) {
    super(props);

    this.listView = React.createRef();
    this.timer = null;
    this.idFieldName = 'itemid';
    this.subs = [];

    // used to keep track of latest request
    this.requestId = null;
    this.subscribedPageName = '';

    // use dynamic server side row model with agGrid
    this.searchOptions = {};

    this.state = {
      attachments: {},
      headers: [],
      resultCount: 0,
      rowData: [],
      listPageName: '',
      gridLayout: null,
      loading: true,
      serverSideMode: this.isServerSide(),
      limit: null,
    };
  }

  componentDidMount() {
    Promise.all([this.getData(), this.getGridLayout()]).then(() => {
      this.setState({ loading: false });
    });
  }

  componentWillUnmount() {
    this.unsubscribe(this.state.listPageName);
  }

  isServerSide = () => {
    return _.get(this.props.page, 'page.List.serverSide', false);
  };

  getGridLayout = () => {
    return ApiService.get('user-grid-layouts', { params: { pageName: this.props.name } }).then(({ data }) => {
      if (data && data.length) {
        this.setState({ gridLayout: data[0].GridLayout });
      }
    });
  };

  getColumnDefs() {
    const { headers } = this.state;

    const pageLayout = this.state.pageMeta.page_layout;
    const layoutObj = new Layout(pageLayout);
    const fieldLookup = layoutObj.getLookup();

    if (headers && headers.length) {
      const columnDefs = [];

      // Add rest of headers
      headers.forEach(header => {
        const { label, helpers, style, hidden, type, ...rest } = header;

        const field = header.name === 'itemid' ? 'itemid' : label;
        const column = {
          headerName: label,
          field,
          cellClass: style || '',
          helpers,
          hide: hidden,
          type,
          ...rest,
        };

        if (hidden) {
          return;
        }

        if (
          column.type &&
          _.intersection(typeof column.type === 'string' ? [column.type] : column.type, [
            'number',
            'currencyColumn',
            'numberColumn',
          ]).length
        ) {
          column.filter = 'agNumberColumnFilter';
        }

        if (column.type === 'basicDateColumn') {
          column.filter = 'agDateColumnFilter';
        }

        // TODO Not needed in v26
        if (column.filterParams) {
          column.filterParams.newRowsAction = 'keep';
        } else {
          column.filterParams = { newRowsAction: 'keep' };
        }

        // Check for Set Filter in saved layout so the filter type can be updated
        if (_.get(this.state.gridLayout, `layout.f.${column.field}.filterType`) === 'set') {
          column.filter = 'agSetColumnFilter';
        }

        const fieldConfig = fieldLookup[header.name];

        // Any field config items that need to be accessed by the cell renderer
        if (fieldConfig) {
          // Put options on column root so they're available for viewing Label
          if (fieldConfig.options) {
            column.options = fieldConfig.options;
          }
        }

        if (column.editable) {
          if (column.options) {
            column.cellEditor = 'agRichSelect';
            column.cellEditorPopup = true;
            column.cellEditorParams = { values: column.options, formatValue };
          }
        }

        columnDefs.push(column);
      });

      return columnDefs;
    }

    // Use default generic headers
    console.warn('Using generic headers, no page.headers found.');
    return [
      { headerName: 'Item ID', field: 'itemid', sort: 'asc' },
      { headerName: 'Description', field: 'Description' },
    ];
  }

  subscribe(name) {
    Socket.list.join(name);
    Socket.db.join('ListAttachment');

    const listCreate = ({ data }) => {
      if (this.listView.current) {
        this.listView.current.addRow(data);
      }
    };

    const listUpdate = ({ data, id }) => {
      if (this.listView.current) {
        this.listView.current.updateRowById(data, id);
      }
    };

    const listRemove = ({ id }) => {
      if (this.listView.current) {
        this.listView.current.removeRowById(id);
      }
    };

    const attachmentUpdate = () => {
      // TODO update attachment count
    };

    this.subs.push(Socket.list.on(`${name}.create`, listCreate, true));
    this.subs.push(Socket.list.on(`${name}.update`, listUpdate, true));
    this.subs.push(Socket.list.on(`${name}.remove`, listRemove, true));
    this.subs.push(Socket.db.on('ListAttachment.create', attachmentUpdate, true));
    this.subs.push(Socket.db.on('ListAttachment.update', attachmentUpdate, true));
    this.subs.push(Socket.db.on('ListAttachment.delete', attachmentUpdate, true));
  }

  unsubscribe(name) {
    this.subs.forEach(f => f());
    this.subs = [];
    Socket.list.leave(name);
    Socket.db.leave('ListAttachment');
  }

  isValidAction(action) {
    const { ValidTypes } = this.props.page.page;

    return !!_.find(ValidTypes, type => type === action);
  }

  isOption(optionName = '', pageMeta) {
    const yamlOptions = _.get(pageMeta, 'yaml.page.Options', []);
    const options = yamlOptions.map(o => o.toLowerCase());

    return options.includes(optionName.toLowerCase());
  }

  canAdd = () => this.isValidAction('add');

  canCopy = () => this.isValidAction('copy');

  canEdit = () => this.isValidAction('update');

  canDelete = () => this.isValidAction('delete');

  canUpload = () => this.isValidAction('upload');

  canRead = () => this.isValidAction('read');

  // TODO: This should eventually be user permission based
  canViewAttachments = () => this.isValidAction('viewattachments');

  isReady() {
    if (this.state.loading || !this.state.listPageName) {
      return false;
    }

    return true;
  }

  fetchData = params => {
    this.requestId = Date.now();

    return ApiService.get(`list/${this.props.name}`, { params });
  };

  getCustomOptions = () => {
    const options = [];
    if (this.isServerSide() && this.state.count <= 250000) {
      if (this.state.serverSideMode) {
        options.push({
          label: (
            <>
              <div style={{ position: 'absolute', left: '6px' }}>
                <i className="far fa-square" />
              </div>
              Enable Grouping
            </>
          ),
          onClick: () => {
            this.setState({ serverSideMode: false, limit: 250000, loading: true }, () => this.getData());
          },
        });
      } else {
        options.push({
          label: (
            <>
              <div style={{ position: 'absolute', left: '6px' }}>
                <i className="far fa-check-square" />
              </div>
              Enable Grouping
            </>
          ),
          onClick: () => {
            this.setState({ serverSideMode: true, limit: null, loading: true }, () => this.getData());
          },
        });
      }
    }

    return options;
  };

  getData = (params = {}) => {
    params.limit = this.state.limit;
    return this.fetchData(params).then(response => {
      const { listPageName, list: rowData, resultCount, attachments, headers, pageMeta, count } = response.data;

      if (this.listView.current && this.listView.current.agGrid && !this.state.serverSideMode) {
        this.listView.current.agGrid.api.setRowData(rowData);
      }

      // Ensure cells aren't editable if ReadOnly is on
      if (this.isOption('ReadOnly', pageMeta)) {
        headers.forEach(h => {
          h.editable = false;
        });
      }

      this.setState({
        rowData,
        listPageName,
        resultCount,
        attachments,
        headers,
        pageMeta,
        loading: false,
        count,
      });

      if (this.subscribedPageName === listPageName) {
        // already subscribed
        return response;
      }

      // new list, subscribe
      this.subscribe(listPageName);
      this.subscribedPageName = listPageName;

      if (listPageName !== this.state.listPageName && this.state.listPageName) {
        this.unsubscribe(this.state.listPageName);
      }

      return response;
    });
  };

  handleRemoveItem = (pageName, itemId) => {
    modal.confirm({
      message: `Delete record '${itemId}'?`,
      headerClass: 'primary',
      callback: () => {
        ApiService.delete(`list/${pageName}/${encodeURIComponent(itemId)}`).then(response => {
          // TODO Should probably be a catch, but sup
          const { data } = response;
          if (_.get(data, 'status') !== 200) {
            toastr.error(_.get(data, 'responses.0.error') || 'Delete Failed');
            return;
          }

          if (this.listView.current) {
            this.listView.current.removeRowById(itemId);
          }
        });
      },
    });
  };

  handleSearch = () => {
    if (this.state.serverSideMode) {
      // hacky
      // this.listView.current.resetDatasource();
    } else {
      this.getData();
    }
  };

  onCellValueChanged = cellEvent => {
    if (cellEvent.source === 'undoUserEdit') {
      return;
    }

    const neededForUpdate = [];
    const payload = [];
    const itemId = cellEvent.data.itemid;
    const fieldName = cellEvent.column.colDef.name;
    const headerName = cellEvent.column.colDef.headerName;
    const columns = cellEvent.columnApi.getAllColumns();

    const newValue = getCellValue(cellEvent.newValue);
    const oldValue = getCellValue(cellEvent.oldValue);

    columns.forEach(c => {
      if (c.colDef.requiredForUpdate) {
        neededForUpdate.push({ fieldName: c.colDef.name, columnName: c.colDef.field });
      }
    });

    neededForUpdate.forEach(column => {
      payload.push({
        collection: 'no',
        name: column.fieldName,
        data: cellEvent.data[column.columnName],
      });
    });

    payload.push({
      collection: 'no',
      name: fieldName,
      data: newValue,
    });

    return ApiService.put(`/list/${this.props.name}/${encodeURIComponent(itemId)}`, payload)
      .then(() => {
        // TODO 200 responses can have update failures
        toastr.success('Cell Updated', `${headerName} from ${oldValue} to ${newValue}`);
      })
      .catch(error => {
        toastr.error('Cell Update Failure', _.get(error, 'response.data.error', error.message));
        cellEvent.node.setDataValue(cellEvent.column, oldValue, 'undoUserEdit');
      });
  };

  render() {
    if (!this.isReady()) {
      return <LoadingPage />;
    }

    return (
      <ListView
        ref={this.listView}
        attachments={this.state.attachments}
        canAdd={this.canAdd()}
        canCopy={this.canCopy()}
        canDelete={this.canDelete()}
        canEdit={this.canEdit()}
        canRead={this.canRead()}
        canUpload={this.canUpload()}
        canViewAttachments={this.canViewAttachments()}
        columnDefs={this.getColumnDefs()}
        count={this.state.resultCount}
        customOptions={this.getCustomOptions()}
        getData={this.fetchData}
        handleRefresh={this.getData}
        handleRemoveItem={this.handleRemoveItem}
        idFieldName={this.idFieldName}
        initialQuery={this.props.query}
        itemName={this.props.name}
        layout={this.state.gridLayout}
        loading={this.state.loading}
        namespace="list"
        refreshTime={_.get(this.props.page, 'page.List.autorefresh')}
        rowData={this.state.rowData}
        search={_.debounce(this.handleSearch, 500)}
        serverSide={this.state.serverSideMode}
        showRowToolbar={
          this.canAdd() ||
          this.canEdit() ||
          this.canDelete() ||
          this.canCopy() ||
          this.canUpload() ||
          this.canViewAttachments() ||
          this.canRead()
        }
        verticalHeaders={!!_.get(this.state.pageMeta, 'page.options.verticalHeaders')}
        onCellValueChanged={this.onCellValueChanged}
        rowButtonCount={_.get(this.props.page, 'page.List.rowButtonCount')}
      />
    );
  }
}

ListContainer.propTypes = {
  name: PropTypes.string,
  page: PropTypes.object,
  query: PropTypes.string,
};

export default ListContainer;
